import { fetchFavoriteProducts } from 'actions/products';
import { Loader } from 'components';
import { useDispatch, useSelector } from 'hooks';
import { isEmpty } from 'lodash';
import React, { FunctionComponent, useMemo, useState, useEffect } from 'react';
import { OfferItemType, ProductCategory, Product, Service } from 'types';

import FavoriteProductsCard from './FavoriteProductsCard';

interface FavoriteProductsCardPropsV2 {
  type: OfferItemType;
  category: ProductCategory;
  selectedItem?: Product | Service;
}

const FavoriteProductsPreview: FunctionComponent<FavoriteProductsCardPropsV2> = ({
  type,
  category,
  selectedItem,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const salesforce = useSelector(({ proposal }) => proposal.salesforce);
  const favoriteProducts = useSelector(({ products }) => products.favorite);
  const funeralType = useSelector(({ proposal }) => proposal.funeralType);
  const isProducts = type === 'products';
  const selectedItemsIds = useSelector(({ proposal }) => proposal['optionalProducts']);

  // Use useMemo to memoize favoriteProductsIds to avoid recalculation on every render
  const favoriteProductsIds = useMemo(
    () =>
      category && !isEmpty(favoriteProducts) && funeralType
        ? favoriteProducts[funeralType][category]
        : [],
    [category, favoriteProducts, funeralType],
  );

  useEffect(() => {
    if (funeralType && category && isEmpty(favoriteProductsIds)) {
      setIsLoading(true);
      dispatch(
        fetchFavoriteProducts({
          category,
          isFavorite: true,
          pricebookId: salesforce?.pricebookId,
          type: funeralType,
        }),
      ).finally(() => setIsLoading(false));
    }
  }, [dispatch, favoriteProductsIds, setIsLoading, salesforce?.pricebookId, category, funeralType]);

  if (!isProducts) {
    return null;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (!favoriteProductsIds || isEmpty(favoriteProductsIds)) {
    return null;
  }

  // if all fav items are selected set flag to true
  const hasFavItems = favoriteProductsIds.every(item => selectedItemsIds.includes(item));

  return favoriteProductsIds && !hasFavItems ? (
    <FavoriteProductsCard
      selectedItemsIds={selectedItemsIds}
      category={category}
      favoriteProductsIds={favoriteProductsIds}
      selectedItem={selectedItem}
    />
  ) : null;
};

export default FavoriteProductsPreview;
